import { z } from 'zod';

import { LIMITS } from '../constants.js';

import { dateOnlySchema, isoDateTimeSchema, timeOfDaySchema, uuidSchema } from './common.js';

/** Minimum estimate when creating or editing a task. */
export const TASK_MIN_HOURS = 0.5;

const taskHoursStoredSchema = z.coerce.number().min(0).max(9999);

const taskHoursInputSchema = z.coerce
  .number()
  .min(TASK_MIN_HOURS, `Минимум ${TASK_MIN_HOURS} ч`)
  .max(9999);

const dueFieldsRefine = <T extends { dueDate?: string | null; dueTime?: string | null }>(
  d: T,
  ctx: z.RefinementCtx,
): void => {
  if (d.dueTime && !d.dueDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Укажите дату вместе со временем',
      path: ['dueTime'],
    });
  }
};

export const taskSchema = z.object({
  id: uuidSchema,
  /** Public numeric id — used in browser URLs (/tasks/100000042). */
  number: z.number().int().positive(),
  projectId: uuidSchema,
  title: z.string().min(1).max(LIMITS.taskTitleMax),
  assignedToId: uuidSchema.nullable(),
  notifyPersonId: uuidSchema.nullable(),
  /** Additional responsible employees (соисполнители). */
  coAssigneeIds: z.array(uuidSchema),
  dueDate: dateOnlySchema.nullable(),
  dueTime: timeOfDaySchema.nullable(),
  notes: z.string().max(LIMITS.taskNotesMax).nullable(),
  timeHours: taskHoursStoredSchema,
  done: z.boolean(),
  /** Allocation month (YYYY-MM) — which month's 160h plan this task counts toward. */
  monthKey: z.string(),
  sortOrder: z.number().int(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  /** Present when the API includes `_count.comments`. */
  commentCount: z.number().int().nonnegative().optional(),
});
export type TaskDto = z.infer<typeof taskSchema>;

export const createTaskSchema = z
  .object({
    projectId: uuidSchema,
    title: z.string().min(1, 'Введите название задачи').max(LIMITS.taskTitleMax),
    assignedToId: uuidSchema.nullable().optional(),
    notifyPersonId: uuidSchema.nullable().optional(),
    coAssigneeIds: z.array(uuidSchema).default([]),
    dueDate: dateOnlySchema.nullable().optional(),
    dueTime: timeOfDaySchema.nullable().optional(),
    notes: z.string().max(LIMITS.taskNotesMax).nullable().optional(),
    timeHours: taskHoursInputSchema.default(TASK_MIN_HOURS),
  })
  .superRefine(dueFieldsRefine);
export type CreateTaskDto = z.infer<typeof createTaskSchema>;

export const updateTaskSchema = z
  .object({
    title: z.string().min(1).max(LIMITS.taskTitleMax),
    assignedToId: uuidSchema.nullable(),
    notifyPersonId: uuidSchema.nullable(),
    coAssigneeIds: z.array(uuidSchema),
    dueDate: dateOnlySchema.nullable(),
    dueTime: timeOfDaySchema.nullable(),
    notes: z.string().max(LIMITS.taskNotesMax).nullable(),
    timeHours: taskHoursInputSchema,
    done: z.boolean(),
    sortOrder: z.number().int(),
    projectId: uuidSchema, // move to another project
  })
  .partial()
  .superRefine(dueFieldsRefine);
export type UpdateTaskDto = z.infer<typeof updateTaskSchema>;

export const duplicateTaskSchema = z.object({
  projectId: uuidSchema,
});
export type DuplicateTaskDto = z.infer<typeof duplicateTaskSchema>;

export const copyLastMonthTasksResponseSchema = z.object({
  created: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  tasks: z.array(taskSchema),
});
export type CopyLastMonthTasksResponseDto = z.infer<typeof copyLastMonthTasksResponseSchema>;

export const updateTaskAllocationMonthSchema = z.object({
  monthKey: z.string().regex(/^\d{4}-\d{2}$/, 'Некорректный формат месяца'),
});
export type UpdateTaskAllocationMonthDto = z.infer<typeof updateTaskAllocationMonthSchema>;

export const allocationMonthContextSchema = z.object({
  lastClosedMonthKey: z.string().regex(/^\d{4}-\d{2}$/).nullable(),
});
export type AllocationMonthContextDto = z.infer<typeof allocationMonthContextSchema>;

// Comments
export const taskCommentSchema = z.object({
  id: uuidSchema,
  taskId: uuidSchema,
  authorId: uuidSchema.nullable(),
  text: z.string().min(1).max(LIMITS.commentTextMax),
  kind: z.enum(['user', 'status']),
  createdAt: isoDateTimeSchema,
});
export type TaskCommentDto = z.infer<typeof taskCommentSchema>;

export const createTaskCommentSchema = z.object({
  taskId: uuidSchema,
  text: z.string().min(1, 'Введите текст комментария').max(LIMITS.commentTextMax),
});
export type CreateTaskCommentDto = z.infer<typeof createTaskCommentSchema>;
